import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, CircularProgress, Box } from '@mui/material';
import Layout from './components/layout/Layout';
import Auth from './common/auth/Auth';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminSettings from './pages/admin/AdminSettings';
import UserDashboard from './pages/user/UserDashboard';
import UserProfile from './pages/user/UserProfile';

// Common pages
import Profile from './pages/common/Profile';

// Super Admin pages
import SuperAdminDashboard from './pages/super-admin/SuperAdminDashboard';
import ChurchesList from './pages/super-admin/ChurchesList';
import CreateChurch from './pages/super-admin/CreateChurch';
import ViewChurch from './pages/super-admin/ViewChurch';
import AllUsers from './pages/super-admin/AllUsers';
import ChurchAdmins from './pages/super-admin/ChurchAdmins';

// Church Admin pages
import ChurchAdminDashboard from './pages/church-admin/ChurchAdminDashboard';
import ChurchProfile from './pages/church-admin/ChurchProfile';
import ChurchMembers from './pages/church-admin/ChurchMembers';
import Announcements from './pages/church-admin/Announcements';
import Sermons from './pages/church-admin/Sermons';
import UserDisplay from './pages/church-admin/UserDisplay';
import PrayerRequestsAdmin from './pages/church-admin/PrayerRequests';
import Attendance from './pages/church-admin/Attendance';
import TransactionsAdmin from './pages/church-admin/Transactions';

// Church User pages
import ChurchSelection from './pages/church-user/ChurchSelection';
import UserDashboardNew from './pages/church-user/UserDashboard';
import AnnouncementsView from './pages/church-user/AnnouncementsView';
import SermonsView from './pages/church-user/SermonsView';
import PrayerRequests from './pages/church-user/PrayerRequests';
import AttendanceUser from './pages/church-user/Attendance';
import Give from './pages/church-user/Give';
import Community from './pages/church-user/Community';
import Notifications from './pages/church-user/Notifications';
import { apiClient } from './lib/apiClient';

const theme = createTheme({
  palette: {
    primary: {
      main: '#d4af39', // Gold/Bronze from logo
      light: '#C4A572',
      dark: '#8B7148',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1E3A5F', // Dark blue from emblem
      light: '#2E5A8F',
      dark: '#142840',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#f8fafc',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C2C2C',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: [
      '"Inter"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
    },
    h2: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
    },
    h3: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
    },
    body1: {
      fontFamily: '"Inter", sans-serif',
    },
    body2: {
      fontFamily: '"Inter", sans-serif',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

function ProtectedRoute({ children, requireRole = null, user }) {
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (requireRole) {
    const roles = Array.isArray(requireRole) ? requireRole : [requireRole];
    if (!roles.includes(user.role)) {
      // Redirect based on user's actual role
      if (user.role === 'super_admin') {
        return <Navigate to="/super-admin/dashboard" replace />;
      } else if (user.role === 'church_admin') {
        return <Navigate to="/church-admin/dashboard" replace />;
      } else {
        return <Navigate to="/church-user/dashboard" replace />;
      }
    }
  }

  return children;
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const storedUser = localStorage.getItem('user');

      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          setUser(user); // Set user from localStorage immediately
          
          // Optionally refresh user data from API
          if (user.id) {
            try {
              const userData = await apiClient.getCurrentUser(user.id);
              setUser(userData);
              localStorage.setItem('user', JSON.stringify(userData));
            } catch (error) {
              // If API call fails, keep the user from localStorage
              console.error('Failed to refresh user data:', error);
            }
          }
        } catch (error) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleAuthSuccess = (userData, token) => {
    setUser(userData);
    // Navigate based on role
    let redirectPath = '/auth';
    if (userData.role === 'super_admin') {
      redirectPath = '/super-admin/dashboard';
    } else if (userData.role === 'church_admin') {
      redirectPath = '/church-admin/dashboard';
    } else if (userData.role === 'church_user') {
      redirectPath = '/church-user/dashboard';
    }
    navigate(redirectPath, { replace: true });
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/auth', { replace: true });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route
        path="/auth"
        element={
          user ? (
            (() => {
              if (user.role === 'super_admin') return <Navigate to="/super-admin/dashboard" replace />;
              if (user.role === 'church_admin') return <Navigate to="/church-admin/dashboard" replace />;
              if (user.role === 'church_user') return <Navigate to="/church-user/dashboard" replace />;
              return <Navigate to="/auth" replace />;
            })()
          ) : (
            <Auth onAuthSuccess={handleAuthSuccess} />
          )
        }
      />

      {/* Super Admin Routes */}
      <Route
        path="/super-admin/*"
        element={
          <ProtectedRoute requireRole="super_admin" user={user}>
            <Layout user={user} onLogout={handleLogout}>
              <Routes>
                <Route path="dashboard" element={<SuperAdminDashboard user={user} />} />
                <Route path="churches" element={<ChurchesList user={user} />} />
                <Route path="churches/:id" element={<ViewChurch user={user} />} />
                <Route path="create-church" element={<CreateChurch user={user} />} />
                <Route path="users" element={<AllUsers user={user} />} />
                <Route path="church-admins" element={<ChurchAdmins user={user} />} />
                <Route path="profile" element={<Profile user={user} onLogout={handleLogout} />} />
                <Route path="*" element={<Navigate to="/super-admin/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Church Admin Routes */}
      <Route
        path="/church-admin/*"
        element={
          <ProtectedRoute requireRole="church_admin" user={user}>
            <Layout user={user} onLogout={handleLogout}>
              <Routes>
                <Route path="dashboard" element={<ChurchAdminDashboard user={user} />} />
                <Route path="profile" element={<ChurchProfile user={user} />} />
                <Route path="members" element={<ChurchMembers user={user} />} />
                <Route path="announcements" element={<Announcements user={user} />} />
                <Route path="sermons" element={<Sermons user={user} />} />
                <Route path="prayer-requests" element={<PrayerRequestsAdmin user={user} />} />
                <Route path="attendance" element={<Attendance user={user} />} />
                <Route path="transactions" element={<TransactionsAdmin user={user} />} />
                <Route path="community" element={<Community user={user} />} />
                <Route path="user-display" element={<UserDisplay user={user} />} />
                <Route path="my-profile" element={<Profile user={user} onLogout={handleLogout} />} />
                <Route path="*" element={<Navigate to="/church-admin/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Church User Routes */}
      <Route
        path="/church-user/*"
        element={
          <ProtectedRoute requireRole="church_user" user={user}>
            <Layout user={user} onLogout={handleLogout}>
              <Routes>
                <Route path="dashboard" element={<UserDashboardNew user={user} />} />
                <Route path="profile" element={<Profile user={user} onLogout={handleLogout} />} />
                <Route path="announcements" element={<AnnouncementsView user={user} />} />
                <Route path="sermons" element={<SermonsView user={user} />} />
                <Route path="prayer-requests" element={<PrayerRequests user={user} />} />
                <Route path="attendance" element={<AttendanceUser user={user} />} />
                <Route path="give" element={<Give user={user} />} />
                <Route path="community" element={<Community user={user} />} />
                <Route path="notifications" element={<Notifications user={user} />} />
                <Route path="*" element={<Navigate to="/church-user/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Legacy Admin Routes (for backward compatibility) */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requireRole={['super_admin', 'church_admin']} user={user}>
            <Layout user={user} onLogout={handleLogout}>
              <Routes>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Legacy User Routes (for backward compatibility) */}
      <Route
        path="/user/*"
        element={
          <ProtectedRoute user={user}>
            <Layout user={user} onLogout={handleLogout}>
              <Routes>
                <Route path="dashboard" element={<UserDashboard />} />
                <Route path="profile" element={<UserProfile />} />
                <Route path="*" element={<Navigate to="/user/dashboard" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route
        path="/"
        element={
          user ? (
            (() => {
              if (user.role === 'super_admin') return <Navigate to="/super-admin/dashboard" replace />;
              if (user.role === 'church_admin') return <Navigate to="/church-admin/dashboard" replace />;
              if (user.role === 'church_user') return <Navigate to="/church-user/dashboard" replace />;
              return <Navigate to="/auth" replace />;
            })()
          ) : (
            <Navigate to="/auth" replace />
          )
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ backgroundColor: 'rgb(248, 247, 247)', minHeight: '100vh' }}>
        <Router>
          <AppContent />
        </Router>
      </Box>
    </ThemeProvider>
  );
}

export default App;
